import { useState, useEffect, useCallback, useMemo } from "react"
import PageLayout from "pages/layout/PageLayout"
import { useLocation, useParams } from "react-router-dom"
import { useAppSelector } from "hooks/useApp"
import { PATH } from "constants/path"
import { BreadcrumbsDetail } from "components/Breadcrumbs/types"
import { HeaderBreadcrumbInfo } from "components/HeaderInfo/HeaderInfo"
import Breadcrumbs from "components/Breadcrumbs"
import { useBoolean, useNumber, useString } from "helpers/hooks"
import {
  GroupOrganizationProject,
  GroupsOrganizationResponse,
} from "pages/groups/groups.type"
import {
  getGroupsOrganizationMiddleware,
  getGroupsOrganizationProjectMiddleware,
  getUserAddGroupsOrganizationMiddleware,
  postUnassignProjectFromGroupsOrganizationMiddleware,
} from "pages/groups/groups.api"
import ButtonHasIcon from "components/Button/ButtonHasIcon"
import { SearchInput } from "components/Search/SearchInput"
import SimpleTable, { SimpleTableProps } from "components/Table/SimpleTable"
import { TippyCustomzie } from "components/TippyCustomzie"
import ActionItem from "pages/projects/molecules/ActionItem"
import { ReactComponent as IconActionThreeDot } from "assets/images/icons/icon-three-dots.svg"
import { Avatar } from "components/Avatar"
import { AssignToGroupModal } from "./modal/AssignToGroupModal"
import { configureStore } from "stores/configureStore"
import { closeModal, openModal } from "reducers/modal"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { Pagination } from "components/Pagination/Pagination"
import { ReactComponent as IconMenuGroup } from "assets/images/icons/group-project.svg"
import { formatDate, getPrivateKeyByUserId } from "helpers/utils"
import { pushTo } from "helpers/history"
import { filter, orderBy, slice, some } from "lodash"
import { AssignToUserModal } from "./modal/AssignToUserModal"
import {
  EncryptionKeys,
  encryptionController,
} from "controllers/EncryptionController"
import { KeyEncryptionType } from "types"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { ActivityLogType, activityLogHelper } from "helpers/activity_log"
import { postActionLogMiddleware } from "components/activity-log/activity-log.api"
import { twMerge } from "tailwind-merge"
import { useWindowSize } from "helpers/hooks"

export const AssignToGroupPage = () => {
  const { isMobile } = useWindowSize()
  const location: any = useLocation()
  const userInfo = useAppSelector((state) => state.userInfo)
  const params = useParams<{
    projectId: string
  }>()
  const projectId = params.projectId || ""
  const isProjectPage = location.state?.isProjectPage || false
  const projectName = useString("")
  const isAssignToGroup = useBoolean(false)
  const isAssignToUser = useBoolean(false)
  const [groups, setGroups] = useState<GroupsOrganizationResponse[]>([])
  const [users, setUsers] = useState<
    {
      id: string
      fullname: string
      email: string
    }[]
  >([])
  const [groupProjects, setGroupProjects] = useState<
    GroupOrganizationProject[]
  >([])

  const [showTippy, setShowTippy] = useState<string[]>([])
  const searchInput = useString("")
  const page = useNumber(1)
  const pageSize = useNumber(25)
  const totalPage = useNumber(0)
  const [sortState, setSortState] = useState<{
    dataIndex: string
    order: "asc" | "desc" | null
  }>({
    dataIndex: "",
    order: null,
  })
  const showTippyAdd = useBoolean(false)
  const projectDecrypted = useString("")

  useEffect(() => {
    if (!projectId || !userInfo?.id) {
      return
    }
    fetchData(true, true)
  }, [projectId, userInfo?.id])

  const fetchData = async (isFetchGroup: boolean, isFetchUser: boolean) => {
    try {
      const projectResponse = await getGroupsOrganizationProjectMiddleware(
        projectId
      )
      const userVaultKey = localStorage.getItem(
        EncryptionKeys.userVaultKey
      ) as string
      const privateKeyPem: any = await getPrivateKeyByUserId()
      const projectKey = projectResponse.projectKey
      const projectKeyType = projectResponse.projectKeyType
      let decrypted = projectKey
      if (projectKeyType === KeyEncryptionType.ProjectShareKey) {
        decrypted = privateKeyPem.decrypt(projectKey, "RSA-OAEP")
      } else {
        decrypted = await encryptionGCMHelper.decryptContext(
          userVaultKey,
          projectKey
        )
      }
      if (decrypted === projectKey) {
        pushTo(PATH.projects)
        return
      }
      projectDecrypted.setValue(decrypted)
      const groupOrganizations = projectResponse.groupOrganizations
      projectName.setValue(projectResponse.projectName)
      setGroupProjects(
        groupOrganizations.map((el) => {
          return {
            ...el,
            type: el.group_organization_id ? "Group" : "User",
          }
        })
      )
      page.setValue(1)
      totalPage.setValue(groupOrganizations.length)
      if (isFetchGroup) {
        const groupsResponse = await getGroupsOrganizationMiddleware({
          page: 1,
          pageSize: 1,
          is_full: true,
          search: "",
        })
        setGroups(groupsResponse.data)
      }
      if (isFetchUser) {
        const userResponse = await getUserAddGroupsOrganizationMiddleware("all")
        setUsers(userResponse)
      }
    } catch (err: any) {
      pushTo(PATH.projects)
    }
  }

  const handleSearchChange = useCallback(
    (value: string) => {
      searchInput.setValue(value)
      page.setValue(1)
    },
    [page, searchInput]
  )

  const handleSearchClear = useCallback(() => {
    searchInput.setValue("")
    page.setValue(1)
  }, [page, searchInput])

  const handleChangeDataWhenAssignToGroup = () => {
    fetchData(false, false)
  }
  const handleRemoveGroupOrganization =
    (newGroupProjectId: string, type: string, name: string) => () => {
      handleClickTippy(newGroupProjectId)
      configureStore.dispatch(
        openModal({
          type: "Delete",
          props: {
            deleteModal: {
              title:
                type === "Group"
                  ? `This action will unassign the project from its current group. Group-specific permissions or settings will no longer apply.`
                  : `This action will unassign the project from its current user. User-specific permissions or settings will no longer apply.`,
              label:
                type === "Group"
                  ? `Unassign group from project`
                  : `Unassign user from project`,
              content: `This action cannot be undone.`,
              onSubmit: handleUnassignFromGroup(newGroupProjectId, type, name),
              styleTitle: { textAlign: "center" },
              titleButton: "Unassign",
            },
          },
        })
      )
    }

  const handleUnassignFromGroup =
    (newGroupProjectId: string, type: string, name: string) => async () => {
      try {
        await postUnassignProjectFromGroupsOrganizationMiddleware(
          newGroupProjectId
        )
        const content = await encryptionController().encrypt(
          activityLogHelper.toLogMessage(
            type === "Group"
              ? ActivityLogType.UnAssignProjectFromGroup
              : ActivityLogType.UnAssignProjectFromUser,
            type === "Group"
              ? {
                  groupName: name,
                }
              : {
                  userName: name,
                }
          ),
          {
            dataType: "string",
            type: "project",
            encryptionKey: projectDecrypted.value,
          }
        )
        await postActionLogMiddleware(
          {
            content: content,
            created_by: localStorage.getItem("user_id") || "",
            model: "projects",
            relation_id: projectId,
          },
          false
        )
        closeModal()
        setGroupProjects((prev) =>
          prev.filter((group) => group.id !== newGroupProjectId)
        )

        toast(
          <LabelNotificationPage
            messenger={
              type === "Group"
                ? "Unassign project from group successfully!"
                : "Unassign project from user successfully!"
            }
            type="success"
          />
        )
      } catch (error: any) {
        toast(
          <LabelNotificationPage
            messenger={
              error.response?.data?.message ||
              (type === "Group"
                ? "Unassign project from group failed!"
                : "Unassign project from user failed!")
            }
            type="error"
          />
        )
      }
    }

  const filteredGroupOrganizations = useMemo(() => {
    const keyword = searchInput.value.trim().toLowerCase()
    if (!keyword) return groupProjects

    const fieldsToSearch = ["name", "code", "email", "type"]

    return filter(groupProjects, (item) =>
      some(fieldsToSearch, (field) =>
        item[field]?.toLowerCase().includes(keyword)
      )
    )
  }, [groupProjects, searchInput.value])

  // --- Sorting --------------------------------------------------------
  const sortedGroupOrganizations = useMemo(() => {
    const { order, dataIndex } = sortState
    if (!order || !dataIndex) return filteredGroupOrganizations

    return orderBy(
      filteredGroupOrganizations,
      [(item) => (item[dataIndex] || "").toLowerCase()],
      [order === "asc" ? "asc" : "desc"]
    )
  }, [filteredGroupOrganizations, sortState])

  // --- Pagination sync ------------------------------------------------
  useEffect(() => {
    const totalItems = filteredGroupOrganizations.length
    totalPage.setValue(totalItems)

    const maxPage =
      totalItems === 0 ? 1 : Math.max(1, Math.ceil(totalItems / pageSize.value))

    if (page.value > maxPage) {
      page.setValue(maxPage)
    }
  }, [filteredGroupOrganizations, page, pageSize.value, totalPage])

  // --- Paginated Slice ------------------------------------------------
  const paginatedGroupOrganizations = useMemo(() => {
    const start = (page.value - 1) * pageSize.value
    return slice(sortedGroupOrganizations, start, start + pageSize.value)
  }, [sortedGroupOrganizations, page.value, pageSize.value])

  const handleSort = useCallback(
    (dataIndex: string, sortDirection: "asc" | "desc" | null) => {
      setSortState({
        dataIndex,
        order: sortDirection,
      })
      page.setValue(1)
    },
    [page]
  )
  const handleClickTippy = useCallback((value: string) => {
    setShowTippy((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : prev.concat([value])
    )
  }, [])

  const listBreadcrumbs: BreadcrumbsDetail[] = [
    {
      name: isProjectPage ? "Projects" : projectName.value,
      field: "idProject",
      path: isProjectPage ? PATH.projects : PATH.projectComponent,
      value: projectId,
    },
    {
      name: "Assign Groups & Users",
    },
  ]
  const columnsMobile: SimpleTableProps["columns"] = [
    {
      title: "Name",
      dataIndex: "name",
      render: (columnData: GroupOrganizationProject) => {
        return (
          <div className="flex flex-col gap-[8px]">
            <div className="flex items-center gap-[8px]">
              {columnData.group_organization_id ? (
                <div className="flex items-center justify-center w-[30px] h-[30px] bg-[#FDBE4426] rounded-full active-menu-left">
                  <IconMenuGroup width={20} />
                </div>
              ) : (
                <Avatar
                  name={columnData.name ?? ""}
                  avatar={columnData.avatar ?? ""}
                  width="30px"
                  height="30px"
                />
              )}
              <div className="flex flex-col">
                <p
                  className={twMerge(
                    "font-semibold text-[13px] leading-[22px] text-[#111111]",
                    columnData.group_organization_id &&
                      "cursor-pointer hover:underline hover:text-[#0A5AF5]"
                  )}
                  onClick={() => {
                    if (columnData.group_organization_id) {
                      pushTo(`/groups/${columnData.group_organization_id}`)
                    }
                  }}
                >
                  {columnData.group_organization_id
                    ? `${columnData.code} - ${columnData.name}`
                    : columnData.name}
                </p>

                <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
                  {columnData.group_organization_id
                    ? `${columnData.members_count || 0} members`
                    : columnData.email}
                </p>
              </div>
            </div>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Type: <span className="">{columnData.type}</span>
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Assigned At:{" "}
              <span className="">
                {columnData.created_at
                  ? formatDate(columnData.created_at, "DD MMM, yyyy")
                  : "-"}
              </span>
            </p>
          </div>
        )
      },
    },
  ]
  const columns: SimpleTableProps["columns"] = [
    {
      title: "Name",
      dataIndex: "name",
      sortable: true,
      render: (columnData: GroupOrganizationProject) => (
        <div className="flex items-center gap-[8px] pl-[20px]">
          {columnData.group_organization_id ? (
            <div className="flex items-center justify-center w-[30px] h-[30px] bg-[#FDBE4426] rounded-full active-menu-left">
              <IconMenuGroup width={20} />
            </div>
          ) : (
            <Avatar
              name={columnData.name ?? ""}
              avatar={columnData.avatar ?? ""}
              width="30px"
              height="30px"
            />
          )}
          <div className="flex flex-col">
            <p
              className={twMerge(
                "font-semibold text-[13px] leading-[22px] text-[#111111]",
                columnData.group_organization_id &&
                  "cursor-pointer hover:underline hover:text-[#0A5AF5]"
              )}
              onClick={() => {
                if (columnData.group_organization_id) {
                  pushTo(`/groups/${columnData.group_organization_id}`)
                }
              }}
            >
              {columnData.group_organization_id
                ? `${columnData.code} - ${columnData.name}`
                : columnData.name}
            </p>

            <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
              {columnData.group_organization_id
                ? `${columnData.members_count || 0} members`
                : columnData.email}
            </p>
          </div>
        </div>
      ),
    },

    {
      title: "Type",
      dataIndex: "type",
      sortable: true,
      width: "15%",
      render: (columnData: GroupOrganizationProject) => (
        <p className="text-[13px] leading-[22px] text-[#111111]">
          {columnData.type}
        </p>
      ),
    },
    {
      title: "Assigned At",
      dataIndex: "created_at",
      width: "15%",
      render: (columnData: GroupOrganizationProject) => (
        <p className="text-[13px] leading-[22px] text-[#111111]">
          {columnData.created_at
            ? formatDate(columnData.created_at, "DD MMM, yyyy")
            : "-"}
        </p>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      align: "right",
      width: "5%",
      render: (columnData: GroupOrganizationProject) => {
        return (
          <TippyCustomzie
            containerClass="component-card-menu"
            placement="bottom-end"
            interactive
            arrow={false}
            animation="scale"
            visible={showTippy.includes(columnData.id)}
            onClickOutside={() =>
              setShowTippy(showTippy.filter((item) => item !== columnData.id))
            }
            content={
              <div
                style={{
                  background: "#222222",
                  borderRadius: 4,
                  paddingTop: 8,
                  paddingBottom: 8,
                  width: 120,
                }}
              >
                <ActionItem
                  title="Unassign"
                  onClick={handleRemoveGroupOrganization(
                    columnData.id,
                    columnData.type || "",
                    columnData.type === "Group"
                      ? `${columnData.code} - ${columnData.name}`
                      : `${columnData.email}`
                  )}
                />
              </div>
            }
            allowHTML
          >
            <div
              className="flex items-center justify-end cursor-pointer"
              onClick={() => {
                handleClickTippy(columnData.id)
              }}
            >
              <IconActionThreeDot />
            </div>
          </TippyCustomzie>
        )
      },
    },
  ]

  return (
    <PageLayout
      childrenHeader={
        <HeaderBreadcrumbInfo>
          <Breadcrumbs
            listBreadcrumbs={listBreadcrumbs}
            className="flex w-full"
          />
          <div className="flex items-center justify-center gap-[12px]">
            {!isMobile ? (
              <SearchInput
                value={searchInput.value}
                onChange={handleSearchChange}
                onClear={handleSearchClear}
                placeholder="Search by name, email, code, type..."
              />
            ) : null}
            {!isMobile ? (
              <TippyCustomzie
                visible={showTippyAdd.value}
                containerClass="component-card-menu"
                placement="bottom-end"
                animation="scale"
                offset={[0, 7]}
                allowHTML
                arrow={false}
                onClickOutside={() => showTippyAdd.setValue(false)}
                interactive
                content={
                  <div className="bg-[#222222] rounded-4 py-2 w-[117px]">
                    <ActionItem
                      className="hidden md:flex"
                      title="Group"
                      onClick={() => {
                        showTippyAdd.setValue(false)
                        isAssignToGroup.setValue(true)
                      }}
                    />
                    <ActionItem
                      className="hidden md:flex"
                      title="User"
                      onClick={() => {
                        showTippyAdd.setValue(false)
                        isAssignToUser.setValue(true)
                      }}
                    />
                  </div>
                }
              >
                <ButtonHasIcon
                  title="Add"
                  widthButton={117}
                  onClick={(event) => {
                    showTippyAdd.setValue(true)
                  }}
                />
              </TippyCustomzie>
            ) : null}
          </div>
        </HeaderBreadcrumbInfo>
      }
    >
      <div className="flex flex-col p-6">
        <div className="text-black bg-white border border-border-gray rounded-md overflow-auto">
          <SimpleTable
            customClass="storage-usage-table"
            columns={isMobile ? columnsMobile : columns}
            isRequiredTableHeader={isMobile ? false : true}
            dataSource={paginatedGroupOrganizations || []}
            onSort={handleSort}
          />
        </div>
        {totalPage.value ? (
          <Pagination
            total={totalPage.value}
            queryState={{
              page: page.value,
              limit: pageSize.value,
            }}
            handleChangePage={(newPage: number, newLimit: number) => {
              page.setValue(newPage)
              pageSize.setValue(newLimit)
            }}
          />
        ) : null}
      </div>{" "}
      {isAssignToGroup.value ? (
        <AssignToGroupModal
          groups={groups.filter(
            (group) =>
              !groupProjects.some(
                (assigned) =>
                  assigned.group_organization_id === group.id ||
                  assigned.code === group.code
              )
          )}
          isOpen={isAssignToGroup.value}
          onClose={() => isAssignToGroup.setValue(false)}
          projectId={projectId}
          callback={handleChangeDataWhenAssignToGroup}
          projectDecrypted={projectDecrypted.value}
        />
      ) : null}
      {isAssignToUser.value ? (
        <AssignToUserModal
          users={users.filter(
            (user) =>
              !groupProjects.some((assigned) => assigned.user_id === user.id)
          )}
          isOpen={isAssignToUser.value}
          onClose={() => isAssignToUser.setValue(false)}
          projectId={projectId}
          callback={handleChangeDataWhenAssignToGroup}
          projectDecrypted={projectDecrypted.value}
        />
      ) : null}
    </PageLayout>
  )
}
